// Sprint fc-conversation-panel §2 S2 — pure selector over the message
// stream, collecting the documents and links a conversation has "touched"
// (A7, founder's own words). The corpus is LOCKED and enforced in BOTH
// directions by A5b (inclusion) and A5c (exclusion) — see
// docs/sprints/aos-founder-comms/sprint-fc-conversation-panel.md §2 S2.
//
// READ, by `MessageType`:
//   assistant_text, user — free text, run through the existing
//     `detectMdPaths` (doc-viewer allowlist: md, txt, json, png, jpg,
//     jpeg, webp, gif, pdf).
//   tool_use — NARROWLY: only for `{Read, Edit, Write, MultiEdit,
//     NotebookEdit}`, and only the STRING values of `input.file_path` /
//     `input.notebook_path`. Every other `input` key is discarded.
//
// NOT READ, deliberately:
//   tool_result — `content: unknown`, unbounded, can carry a whole file
//     body (founder-first-vision.md §8 point 6).
//   thinking_block, system, error, permission/question cards — no
//     document relation.
//   Any other tool_use name (Bash, Grep, ExitPlanMode, …) — reading
//     `input` by key rather than stringifying it is what keeps command
//     strings, grep patterns and plan bodies off a founder surface.
//
// Links (`https?://` URLs) come from the assistant_text + user corpus
// ONLY — tool payloads are excluded there too. No exported URL extractor
// exists in the repo to reuse (§1.8); this module owns the regex.
//
// Both lists: de-duplicated, most-recent-first, capped at 12. The
// `*Total` fields let the caller render a "+N más" tail.

import type { Message } from '../../../types';
import { detectMdPaths } from '../../../lib/detectMdPaths';
import { asAssistantText, asUserContent, asToolUse } from '../../../lib/messages';

const DOC_TOOL_NAMES = new Set([
  'Read',
  'Edit',
  'Write',
  'MultiEdit',
  'NotebookEdit',
]);

const CAP = 12;

// Deliberately does not chase every RFC-3986 edge case — false negatives
// (a URL not detected) are acceptable, false positives are not. Trailing
// punctuation likely belongs to surrounding prose, not the URL.
const URL_PATTERN = /https?:\/\/[^\s<>"')\]]+/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

export interface CollectedDocLinks {
  docs: string[];
  docsTotal: number;
  links: string[];
  linksTotal: number;
}

/**
 * Reuses `detectMdPaths`' own boundary + allowlist logic instead of
 * re-declaring the extension list here (Horizontal Reuse): a candidate
 * string is a valid doc path when the WHOLE string is one of its own
 * matches.
 */
function isValidDocPath(candidate: string): boolean {
  return detectMdPaths(candidate).some((d) => d.path === candidate);
}

function stripTrailingPunctuation(url: string): string {
  return url.replace(TRAILING_PUNCTUATION, '');
}

function docsInText(text: string): string[] {
  return detectMdPaths(text).map((d) => d.path);
}

function linksInText(text: string): string[] {
  const matches = text.match(URL_PATTERN) ?? [];
  return matches.map(stripTrailingPunctuation).filter((u) => u.length > 0);
}

function docsFromToolUse(m: Message): string[] {
  const tu = asToolUse(m);
  if (!DOC_TOOL_NAMES.has(tu.name)) return [];
  const out: string[] = [];
  const filePath = tu.input.file_path;
  const notebookPath = tu.input.notebook_path;
  if (typeof filePath === 'string' && isValidDocPath(filePath)) {
    out.push(filePath);
  }
  if (typeof notebookPath === 'string' && isValidDocPath(notebookPath)) {
    out.push(notebookPath);
  }
  return out;
}

/** De-duplicate preserving first-seen order, then cap. */
function collect(candidatesNewestFirst: string[]): {
  items: string[];
  total: number;
} {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const c of candidatesNewestFirst) {
    if (seen.has(c)) continue;
    seen.add(c);
    ordered.push(c);
  }
  return { items: ordered.slice(0, CAP), total: ordered.length };
}

export function collectDocLinks(messages: Message[]): CollectedDocLinks {
  const docCandidates: string[] = [];
  const linkCandidates: string[] = [];

  // Iterate newest → oldest so first-seen (post-dedupe) = most recent —
  // A7's "most-recent-first" ordering, and the mitigation for the
  // turn-0 rule-file noise edge case (§10): repeatedly-read files sink
  // below anything touched more recently, and the cap then drops them
  // entirely in a conversation of normal length.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m) continue;
    if (m.type === 'assistant_text') {
      const text = asAssistantText(m).text;
      docCandidates.push(...docsInText(text));
      linkCandidates.push(...linksInText(text));
    } else if (m.type === 'user') {
      const text = asUserContent(m).text;
      docCandidates.push(...docsInText(text));
      linkCandidates.push(...linksInText(text));
    } else if (m.type === 'tool_use') {
      docCandidates.push(...docsFromToolUse(m));
    }
    // tool_result, thinking_block, system, error, permission/question
    // cards: deliberately not read (see module header).
  }

  const docs = collect(docCandidates);
  const links = collect(linkCandidates);
  return {
    docs: docs.items,
    docsTotal: docs.total,
    links: links.items,
    linksTotal: links.total,
  };
}
